import React, { useMemo } from 'react'
import { useWatch } from 'react-hook-form'

import {
  SanityHeroImageOptionDocument,
  SanityHeroTextOptionDocument,
} from '../../../../../../../groq/sanity-dispensaries/types'
import { dispensaryImageUrlFor } from '../../../../../../../lib/sanity'
import { DispensaryHeroFormValues } from '../CustomizeHomePage'
import * as Styled from './styled'

export interface HeroLivePreviewProps {
  imageOptionForSlug: SanityHeroImageOptionDocument | null
  textOptionForSlug: SanityHeroTextOptionDocument | null
  imageOptions: ReadonlyArray<SanityHeroImageOptionDocument>
  textOptions: ReadonlyArray<SanityHeroTextOptionDocument>
  renderEntireImageAsCta: DispensaryHeroFormValues['renderEntireImageAsCta']
}

const HeroLivePreview: React.VFC<HeroLivePreviewProps> = ({
  imageOptionForSlug,
  textOptionForSlug,
  imageOptions,
  textOptions,
  renderEntireImageAsCta,
}) => {
  const formValues = useWatch<DispensaryHeroFormValues>({})
  const heroImage:
    | Pick<
        SanityHeroImageOptionDocument,
        'image' | 'textColor' | 'ctaTextColor' | 'ctaBackgroundColor'
      >
    | undefined = useMemo(() => {
    if (
      formValues.selectedImageOptionId === 'new' ||
      formValues.selectedImageOptionId === imageOptionForSlug?._id
    ) {
      if (!formValues.imageAssetRef) {
        return
      }
      return {
        image: {
          _type: 'image',
          asset: {
            _type: 'reference',
            _ref: formValues.imageAssetRef,
          },
        },
        textColor: formValues.textColor ?? '',
        ctaBackgroundColor: formValues.ctaBackgroundColor ?? '',
        ctaTextColor: formValues.ctaTextColor ?? '',
      }
    }
    return imageOptions.find(
      (heroImageOption) => formValues.selectedImageOptionId === heroImageOption._id,
    )
  }, [
    imageOptionForSlug?._id,
    formValues.ctaBackgroundColor,
    formValues.ctaTextColor,
    formValues.imageAssetRef,
    formValues.selectedImageOptionId,
    formValues.textColor,
    imageOptions,
  ])
  const heroText: Pick<SanityHeroTextOptionDocument, 'text' | 'ctaText'> | undefined =
    useMemo(() => {
      if (
        formValues.selectedTextOptionId === textOptionForSlug?._id ||
        formValues.selectedTextOptionId === 'new'
      ) {
        return {
          text: formValues.text ?? '',
          ctaText: formValues.ctaText ?? '',
        }
      }
      return textOptions.find(
        (heroTextOption) => formValues.selectedTextOptionId === heroTextOption._id,
      )
    }, [
      textOptionForSlug?._id,
      formValues.ctaText,
      formValues.selectedTextOptionId,
      formValues.text,
      textOptions,
    ])

  if (!heroImage || !heroText) {
    return (
      <Styled.OuterContainer>
        <Styled.InnerContainer>
          <Styled.UnavailableText>Preview Unavailable</Styled.UnavailableText>
          <Styled.UnavailableText>Make Your Selections Below!</Styled.UnavailableText>
        </Styled.InnerContainer>
      </Styled.OuterContainer>
    )
  }

  return (
    <Styled.OuterContainer>
      <Styled.Image
        src={
          dispensaryImageUrlFor({ _ref: heroImage.image.asset._ref })
            .width(740)
            .height(250)
            .dpr(2)
            .fit('max')
            .auto('format')
            .url() || ''
        }
      />
      {renderEntireImageAsCta !== 'yes' && (
        <Styled.InnerContainer>
          <Styled.Paragraph textColor={heroImage.textColor}>{heroText.text}</Styled.Paragraph>
          {heroText.ctaText && (
            <Styled.Button
              disabled={true}
              ctaBackgroundColor={heroImage.ctaBackgroundColor}
              ctaTextColor={heroImage.ctaTextColor}
            >
              {heroText.ctaText}
            </Styled.Button>
          )}
        </Styled.InnerContainer>
      )}
    </Styled.OuterContainer>
  )
}

export default HeroLivePreview
